import { useMemo } from 'react';

import { getShareUrlInfo } from '#src/app/shared/share/utils';

import type { ShareChannel, ShareConfig } from '../shared/types';

import type { ShareAction } from './actions';
import { useShareActionsCore } from './actions';
import type { ShareActionComponentProps, ShareLink, ShareTrackingDataOverride, ShareUrlInfo } from './types';
import { useNativeShareFilteredLinks } from './useNativeShareFilteredLinks';

// Mirrors the GraphQL `ShareLink` type — see actions/types.ts.
export type { ShareLink };
export type { ShareActionComponentProps, ShareTrackingDataOverride };

export type ShareActionsOptions<CH extends ShareChannel = ShareChannel> = {
	config?: ShareConfig;
	trackingEventName: string;
	getContent: (channel: CH, type: 'title' | 'body' | 'image', context: { url: string }) => string;
	getTrackingData: (channel: CH) => Readonly<Record<string, boolean | string | number | undefined>>;
	/**
	 * Fires synchronously in the tap's own tick, before the channel's share runs and before any
	 * `await`. Use it for work that must beat the share itself — e.g. promoteOrShare cancels its
	 * pending copy-link redirect here, which has to happen before Instagram's clipboard write yields.
	 * Must stay referentially stable: it feeds `useGetShareActionConfig`, so a changing identity
	 * rebuilds every channel's `ShareAction` component.
	 */
	onBeforeShare?: (channel: CH) => void;
	onSuccess?: (channel: CH) => void | Promise<void>;
	onFailure?: (channel: CH) => void | Promise<void>;
};

/**
 * Builds share actions from server-provided `ShareLinks.links` (typically queried
 * from `Petition.shareLinks` on the middle-end). Long URLs, short URLs, and share
 * IDs all come pre-computed from the server; no client-side URL shortening or
 * shareId extraction happens here — it trusts the server's channel selection.
 * Channels gated by `useIsMobileNativeShareCapable` (`mobileNativeShare`,
 * `instagramStories`, `tiktok`) are filtered out client-side after hydration
 * when native share is unavailable; this may cause those buttons to disappear
 * briefly on devices that lack the Web Share API.
 *
 * When a link's `shortUrl` is null (e.g. the server-side shortener failed or didn't run), the
 * action falls back to the `longUrl`.
 */
export function useShareActions(
	links: readonly ShareLink[],
	{
		trackingEventName,
		getContent,
		getTrackingData,
		onBeforeShare,
		onSuccess,
		onFailure,
		config,
	}: ShareActionsOptions<ShareLink['channel']>,
): ReadonlyArray<ShareAction<ShareLink['channel']>> {
	const configWithDefaults = useMemo(() => config || {}, [config]);
	const filteredLinks = useNativeShareFilteredLinks(links);

	const byChannel = useMemo(() => new Map(filteredLinks.map((link) => [link.channel, link])), [filteredLinks]);
	const channels = useMemo(() => filteredLinks.map((link) => link.channel), [filteredLinks]);

	const getUrl = useMemo(
		() => (channel: ShareLink['channel']) => {
			const link = byChannel.get(channel);
			if (!link) throw new Error(`useShareActions: no link for channel "${channel}"`);
			return link.longUrl;
		},
		[byChannel],
	);

	const getShortUrlInfo = useMemo(
		() =>
			(channel: ShareLink['channel']): ShareUrlInfo => {
				const link = byChannel.get(channel);
				if (!link) throw new Error(`useShareActions: no link for channel "${channel}"`);
				return getShareUrlInfo(link);
			},
		[byChannel],
	);

	return useShareActionsCore<ShareLink['channel']>(channels, {
		getUrl,
		getShortUrlInfo,
		getTrackingData,
		getContent,
		trackingEventName,
		onBeforeShare,
		onSuccess,
		onFailure,
		config: configWithDefaults,
	});
}

export type { ShareAction };
